import { z } from "zod";
import { zodQueryStringArray } from "../utils/schema.js";
import {
  PaginationMetaResponseSchema,
  PaginationQuerySchema,
} from "./Pagination.js";

export const factInsightSeverities = ["low", "medium", "high"] as const;

export const factInsightSeverityRank: Record<FactInsightSeverity, number> = {
  high: 0,
  medium: 1,
  low: 2,
};

export const factInsightStatuses = ["open", "dismissed", "resolved"] as const;

export const FactInsightStatusSchema = z.enum(factInsightStatuses);

export const FactInsightWordingSchema = z.union([
  z.object({
    action: z.string(),
    confidence: z.string(),
    reason: z.string(),
    userSuggestion: z.string(),
  }),
  z.object({
    suggestedBlocks: z.array(
      z.object({ key: z.string(), title: z.string(), definition: z.string() }),
    ),
  }),
]);

export const FactInsightLlmSchema = z.object({
  status: z.string().optional(),
  action: z.string().optional(),
  confidence: z.string().optional(),
  reason: z.string().optional(),
  userSuggestion: z.string().optional(),
  error: z.string().optional(),
  /** ML reuses the stored wording while this matches */
  inputHash: z.string().optional(),
  wording: FactInsightWordingSchema.optional(),
});

export const FactInsightGapSchema = z.object({
  subtopics: z.array(
    z.object({
      key: z.string(),
      title: z.string(),
      status: z.string(),
      matchMethod: z.string(),
      semanticSimilarity: z.number().nullable(),
      semanticScoreMargin: z.number().nullable(),
      evidenceBlockIds: z.array(z.string()),
    }),
  ),
  suggestedBlocks: z.array(
    z.object({
      key: z.string(),
      title: z.string(),
      definition: z.string(),
      existingStackId: z.string().nullable(),
      /** Set when no active stack fits and ML proposes a new one */
      newStackTitle: z.string().nullable(),
    }),
  ),
});

export const FactInsightMetadataSchema = z.object({
  llm: FactInsightLlmSchema.optional(),
  gap: FactInsightGapSchema.optional(),
});

export const FactInsightSchema = z.object({
  id: z.uuid(),
  type: z.string(),
  severity: z.enum(factInsightSeverities),
  title: z.string(),
  description: z.string(),
  suggestion: z.string(),
  /** Prefixed with the ML node type, fact::<id> */
  nodeIds: z.array(z.string()),
  blockIds: z.array(z.uuid()),
  metadata: FactInsightMetadataSchema,
  status: FactInsightStatusSchema,
  createdAt: z.date(),
  resolvedAt: z.date().nullable(),
  reviewerId: z.uuid().nullable(),
  reviewedAt: z.date().nullable(),
});

export const FactInsightRunItemSchema = FactInsightSchema.pick({
  type: true,
  severity: true,
  title: true,
  description: true,
  suggestion: true,
  nodeIds: true,
  blockIds: true,
  metadata: true,
}).extend({ fingerprint: z.string() });

export const ListFactInsightsToolInputSchema = z.object({
  severity: z
    .array(z.enum(factInsightSeverities))
    .optional()
    .describe("Keep only these severities."),
  status: FactInsightStatusSchema.default("open").describe(
    "open by default. dismissed ones were hidden by a person, resolved ones are fixed.",
  ),
  limit: z.number().int().min(1).max(100).default(50),
  offset: z.number().int().min(0).optional(),
});

export const FactInsightOutputSchema = z.object({
  id: FactInsightSchema.shape.id,
  type: FactInsightSchema.shape.type,
  severity: FactInsightSchema.shape.severity,
  status: FactInsightSchema.shape.status,
  title: FactInsightSchema.shape.title,
  description: FactInsightSchema.shape.description,
  suggestion: FactInsightSchema.shape.suggestion,
  factIds: z
    .array(z.string())
    .describe(
      "Can name a fact version that has since been replaced, so read the current fact with list_facts on blockIds before acting on it.",
    ),
  blockIds: FactInsightSchema.shape.blockIds,
});

const factNodePrefix = "fact::";

export function toFactInsightOutput(insight: FactInsight): FactInsightOutput {
  return {
    id: insight.id,
    type: insight.type,
    severity: insight.severity,
    status: insight.status,
    title: insight.title,
    description: insight.description,
    suggestion: insight.suggestion,
    factIds: insight.nodeIds
      .filter((id) => id.startsWith(factNodePrefix))
      .map((id) => id.slice(factNodePrefix.length)),
    blockIds: insight.blockIds,
  };
}

/**
 * ========================================
 * PUBLIC API SCHEMAS
 * ========================================
 */
export const GetInsightsRequestSchema = z.object({
  ...PaginationQuerySchema.shape,
  severity: zodQueryStringArray(z.enum(factInsightSeverities)),
  status: FactInsightStatusSchema.optional(),
});

export const GetInsightsResponseSchema = z.object({
  ...PaginationMetaResponseSchema.shape,
  items: z.array(FactInsightOutputSchema),
});
export type GetInsightsRequest = z.infer<typeof GetInsightsRequestSchema>;
export type GetInsightsResponse = z.infer<typeof GetInsightsResponseSchema>;
/**
 * ========================================
 * END PUBLIC API SCHEMAS
 * ========================================
 */

export type FactInsightSeverity = (typeof factInsightSeverities)[number];
export type FactInsightStatus = (typeof factInsightStatuses)[number];
export type FactInsightLlm = z.infer<typeof FactInsightLlmSchema>;
export type FactInsightWording = z.infer<typeof FactInsightWordingSchema>;
export type FactInsightGap = z.infer<typeof FactInsightGapSchema>;
export type FactInsightMetadata = z.infer<typeof FactInsightMetadataSchema>;
export type FactInsight = z.infer<typeof FactInsightSchema>;
export type FactInsightRunItem = z.infer<typeof FactInsightRunItemSchema>;
export type ListFactInsightsToolInput = z.infer<
  typeof ListFactInsightsToolInputSchema
>;
export type FactInsightOutput = z.infer<typeof FactInsightOutputSchema>;
